import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { useNominationForm } from '../../context/NominationFormContext';
import FileUploadWidget from './FileUploadWidget';
import FileUploadErrorBoundary from '../common/FileUploadErrorBoundary';

/**
 * DocumentsForm component
 * Second step of the nomination form for document selection
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Initial form data
 * @param {Function} props.onChange - Function to call when form data changes
 * @param {Boolean} props.showValidation - Whether to trigger validation
 */
const DocumentsForm = ({ formData = {}, onChange, showValidation = false }) => {
  const toast = useToast();
  const { dispatch } = useNominationForm();
  
  // We only need the setter function for isValid
  const [, setIsValid] = useState(false);
  
  // دالة مساعدة للتحقق من وجود ملف فعلي
  const isValidFile = (docData) => {
    return docData && 
           docData.file && 
           docData.file instanceof File && 
           docData.file.size > 0;
  };
  

  const [documents, setDocuments] = useState({
    personal_photo: formData.personal_photo || null,
    cv: formData.cv || null,
    security_clearance: formData.security_clearance || null
  });

  // التحقق من رفع جميع المستندات المطلوبة مع التأكد من وجود ملف فعلي
  useEffect(() => {
    // استخدام الدالة المساعدة للتحقق من صحة الملفات
    // الصورة الشخصية اختيارية
    const hasValidCV = isValidFile(documents.cv);
    const hasValidSecurityClearance = isValidFile(documents.security_clearance);
    
    // الصورة الشخصية اختيارية، لذا نتحقق فقط من المستندات المطلوبة
    const allUploaded = hasValidCV && hasValidSecurityClearance;
    setIsValid(allUploaded);
    
    if (typeof onChange === 'function') {
      onChange(documents, allUploaded);
    }
  }, [documents, onChange]);

  // Handle file selection
  const handleFileSelect = (file, fileType) => {
    if (!file) {
      // File was removed
      setDocuments(prev => ({
        ...prev,
        [fileType]: null
      }));
      return;
    }

    // Update documents state with the new file data
    setDocuments(prev => ({
      ...prev,
      [fileType]: {
        file: file,
        name: file.name,
        type: file.type,
        size: file.size
      }
    }));

    // Update the form context
    dispatch({
      type: 'UPDATE_STEP_DATA',
      payload: {
        step: 2,
        data: {
          ...documents,
          [fileType]: {
            file: file,
            name: file.name,
            type: file.type,
            size: file.size
          }
        },
        isValid: true
      }
    });

    // Show success message
    toast({
      title: "تم اختيار الملف بنجاح",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };


  return (
    <FileUploadErrorBoundary
      userFriendlyMessage="حدث خطأ أثناء تحميل نموذج المستندات. يرجى إعادة تحميل الصفحة."
      onRetry={() => window.location.reload()}
    >
      <Box>
        <Heading as="h2" size="lg" mb={6} textAlign="center">
          المستندات المطلوبة
        </Heading>
        
        <Text mb={6}>
          يرجى رفع المستندات التالية لإكمال طلب الترشيح. جميع المستندات مطلوبة.
        </Text>
        
        <Stack spacing={6}>
          {/* Personal Photo Selection (Optional) */}
          <FileUploadWidget
            label="الصورة الشخصية (اختياري)"
            fileType="personal_photo"
            acceptedFileTypes="image/*"
            onFileSelect={handleFileSelect}
            initialFileUrl={documents.personal_photo?.url}
            helperText="يمكنك اختيار صورة شخصية حديثة بخلفية بيضاء (اختياري)"
            errorMessage=""
            isRequired={false}
            isInvalid={false}
          />
          
          {/* CV Selection */}
          <FileUploadWidget
            label="السيرة الذاتية"
            fileType="cv"
            acceptedFileTypes=".pdf,.PDF,application/pdf,application/x-pdf"
            onFileSelect={handleFileSelect}
            initialFileUrl={documents.cv?.url}
            helperText="يرجى اختيار سيرتك الذاتية المحدثة بصيغة PDF"
            errorMessage="السيرة الذاتية مطلوبة"
            isRequired={true}
            isInvalid={showValidation && !documents.cv}
          />
          
          {/* Security Clearance Selection */}
          <FileUploadWidget
            label="المسح الأمني"
            fileType="security_clearance"
            acceptedFileTypes=".pdf,.PDF,application/pdf,application/x-pdf"
            onFileSelect={handleFileSelect}
            initialFileUrl={documents.security_clearance?.url}
            helperText={
              <>
                يرجى تحميل <a href="https://majalisna-files-2025.s3.me-south-1.amazonaws.com/public-folder/%D9%86%D9%85%D9%88%D8%B0%D8%AC+%D8%A7%D9%84%D9%85%D8%B3%D8%AD+%D8%A7%D9%84%D8%A3%D9%85%D9%86%D9%8A+1.docx" target="_blank" rel="noreferrer" style={{color: 'blue', textDecoration: 'underline'}}>نموذج المسح الأمني</a> وتعبئته ثم إعادة رفعه
              </>
            }
            errorMessage="المسح الأمني مطلوب"
            isRequired={true}
            isInvalid={showValidation && !documents.security_clearance}
          />
        </Stack>
      </Box>
    </FileUploadErrorBoundary>
  );
};

export default DocumentsForm;